import React, { useState, useMemo, useImperativeHandle } from 'react'
import { useFormik, FormikProvider, FieldArray } from 'formik'
import { dynamicSchema } from './seller-market-product-variant-attribute-form-schema'
import SellerMarketProductService from 'domains/seller-market-product/services'
import { Button } from 'reactstrap'
import MarketProductVariantField from 'domains/seller-market-product/molecules/variant-field'
import PropTypes from 'prop-types'
import withLoader from 'hocs/with-loader'

const propTypes = {
  formRef        : PropTypes.object,
  onChange       : PropTypes.func,
  attributes     : PropTypes.array,
  productSizes   : PropTypes.array,
  isAlreadyLive  : PropTypes.bool,
  initialValues  : PropTypes.object,
  disabledFields : PropTypes.shape({
    variants : PropTypes.object,
    button   : PropTypes.bool
  })
}

const defaultProps = {
  initialValues  : {},
  disabledFields : {}
}

/**
 * Market Product variant form.
 * this component use imperative way to submit.
 * the submit button should be handle on the parent componet
 * @param {string} formRef - Reference ID from useRef
 * @param {func} onChange - Handler to change form value state in the parent
 * @param {array} attributes - collection of Product Attribute
 * @param {array} productSizes - collection of Product Size
 * @param {object} initialValues - Formik initial values
 * @param {bool} disabledFields - consist of disabled fields
 */
const MarketProductVariantAttributeForm = ({
  formRef,
  onChange,
  attributes,
  productSizes,
  initialValues,
  disabledFields
}) => {
  const [ validationSchema, setValidationSchema ] = useState()
  const formik                                    = useFormik({
    enableReinitialize : false,
    initialValues      : initialValues,
    validationSchema   : validationSchema,
    onSubmit           : () => watchForm()
  })

  const {
    values: {
      variants
    }
  } = formik

  useImperativeHandle(formRef, () => ({
    submit: async () => {
      await formik.submitForm()
    },
    reload  : () => reload(),
    isValid : formik.isValid
  }))


  /**
   * Trigger to change validation schema and values depending on the attributes props
   * @param {bool} isNewValues - Flag whether it should change new values or not
   */
  const reload = () => {

    const generatedValidationSchema = dynamicSchema({ attributes, productSizes })
    setValidationSchema(generatedValidationSchema)
  }

  /** Let the parent to 'watch' the form values when submitting */
  const watchForm = () => {
    if (onChange) {
      onChange({
        values: formik.values
      })
    }
  }

  const measurementProps = useMemo(() => ({
    parentName            : 'variants',
    productSizes          : productSizes,
    measurementAttributes : attributes
  }), [ attributes, productSizes ])

  return (
    <FormikProvider value={ formik }>
      <form ref={ formRef }>
        <FieldArray
          name={ 'variants' }
          validateOnChange={ false }
          render={ (arrayHelpers) => (
            <>
              <MarketProductVariantField
                { ...measurementProps }
                disabledFields={ disabledFields?.variants }
                arrayHelpers={ arrayHelpers }
                variants={ variants }
              />

              {
                /** only allow adding new variant if
                 * - the category has size option
                 * - disabled field is false
                 */
                productSizes.length > 0 && !disabledFields?.button
                  ? (
                    <div className='pt-3'>
                      <small>Kamu dapat menambahkan variant lainnya</small>
                      <Button
                        block
                        outline
                        color='primary'
                        onClick={ () => arrayHelpers.push(
                          // Adding initial value for the new variat value
                          SellerMarketProductService.buildFormikAttributeInitialValue(
                            attributes,
                            {
                              size     : '',
                              quantity : ''
                            }
                          )) }
                      >
                        { 'Tambah variant' }
                      </Button>
                    </div>
                  )
                  : null
              }
            </>
          ) }
        />
      </form>
    </FormikProvider>
  )
}

MarketProductVariantAttributeForm.propTypes    = propTypes
MarketProductVariantAttributeForm.defaultProps = defaultProps

export default withLoader(MarketProductVariantAttributeForm)
